import React, {useEffect, useState} from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core'
import useStyles from 'components/Table/useTableStyles'
import {useTranslation} from 'react-i18next'
import {isoStringToDateAndTimeString} from 'helpers/dateHelper'
import {formatPriceNumber} from 'helpers/uiHelper'
import {
  fetchProductDetailApi,
  GoodsDisplayInfoType,
  ProductPurchaseHistoryResponse,
} from 'api/productApi'
import {
  TYPE_GOOD_TYPE,
  TYPE_PAYMENT_STATUS,
  TypeGoodsType,
  TypePaymentStatus,
  UNIT_PRICES,
} from 'pages/ProductsPages/constant'
import {usdToCent} from 'helpers/paymentHelper'

interface IDataTableProps {
  onCancelPurchase: (item: any) => void
  onShowInvoice: (item: any) => void
  items: ProductPurchaseHistoryResponse[]
  onNavigateProductDetail: (id: string) => void
  onUpdateCsvData: (data: {[key: string]: string | number}[]) => void
  currentPage: number
  size: number
  listOrgs: OrganizationV2[]
}

const DataTable = ({
  onCancelPurchase,
  items,
  onNavigateProductDetail,
  currentPage,
  size,
  onUpdateCsvData,
  listOrgs,
  onShowInvoice,
}: IDataTableProps) => {
  const classes = useStyles()
  const {t, i18n} = useTranslation()
  const [listGoodsName, setListGoodsName] = useState<
    {
      id: string
      name: GoodsDisplayInfoType[]
    }[]
  >([])
  const tableHeader = [
    {
      id: 'number',
      label: t('ICustomerNumber'),
    },
    {
      id: 'paymentStatus',
      label: t('IPaymentStatus'),
    },
    {
      id: 'organizationName',
      label: t('ICustomerOrganizationName'),
    },
    {
      id: 'account',
      label: t('IAccount'),
    },
    {
      id: 'productClassification',
      label: t('IProductClassification'),
    },
    {
      id: 'productName',
      label: t('IProductNameSelect'),
    },
    {
      id: 'paidDate',
      label: t('IPaidDate'),
    },
    {
      id: 'orderNumber',
      label: t('IOrderNumber'),
    },
    {
      id: 'purchaseAmount',
      label: t('IPurchaseAmount'),
    },
    {
      id: 'cancelAmount',
      label: t('ICancelAmount'),
    },
    {
      id: 'cancelPurchase',
      label: t('ICancelPurchase'),
    },
    {
      id: 'invoice',
      label: t('IProofOfPayment'),
    },
  ]

  const getProductClassitication = (type: TypeGoodsType) => {
    if (type === TYPE_GOOD_TYPE.CHARGE) {
      return t('IChargeV1')
    }
    if (type === TYPE_GOOD_TYPE.PACKAGE) {
      return t('IPackage')
    }
    return ''
  }

  const getProductStatus = (status: TypePaymentStatus) => {
    switch (status) {
      case TYPE_PAYMENT_STATUS.APPROVED:
        return t('IPaymentCompleted')
      case TYPE_PAYMENT_STATUS.ONGOING:
        return t('IWriting')
      case TYPE_PAYMENT_STATUS.REFUNDED:
        return t('IPurchaseCancel')
      default:
        return status
    }
  }

  const getOrganizationName = (orgId: string) => {
    const org = listOrgs.find(
      (organization) => organization.id === Number(orgId),
    )
    return org?.name || ''
  }

  const getGoodsName = (orderId: string) => {
    const goods = listGoodsName.find((item) => item.id === orderId)
    return (
      goods?.name.find(
        (info) => info.langCode === (i18n.language === 'ko' ? 'ko' : 'en'),
      )?.name || ''
    )
  }

  useEffect(() => {
    const fetchGoodsNames = async () => {
      const names = await Promise.all(
        items.map(async (item) => {
          try {
            const res = await fetchProductDetailApi({
              goodsId: item.order.orderItems[0].goodsId,
            })
            return {id: item.order.orderId, name: res.data.goods.displayInfos}
          } catch (error) {
            console.error(error)
            return {
              id: item.order.orderId,
              name: [],
            }
          }
        }),
      )
      setListGoodsName(names)
    }
    fetchGoodsNames()
  }, [items])

  useEffect(() => {
    const csvData = items.map((item, index) => ({
      [t('ICustomerNumber')]: currentPage * size + (items.length - index),
      [t('IPaymentStatus')]: getProductStatus(item.payment.status),
      [t('ICustomerOrganizationName')]: getOrganizationName(item.order.orgId),
      [t('IAccount')]: item.payment.customer.email,
      [t('IProductClassification')]: getProductClassitication(
        item.order.orderItems[0].goodsType,
      ),
      [t('IProductNameSelect')]: getGoodsName(item.order.orderId),
      [t('IPaidDate')]: isoStringToDateAndTimeString(
        String(item.order.orderAt),
      ),
      [t('IOrderNumber')]: item.payment.paymentNo,
      [t('IPurchaseAmount')]: `${
        item.payment.price.price > 0 ? `${item.payment.price.currency}` : ''
      } ${formatPriceNumber(
        item.payment.price.currency === UNIT_PRICES.USD
          ? usdToCent(item.payment.price.price)
          : item.payment.price.price,
      )}`,
      [t('ICancelAmount')]:
        item.cancellations.length > 0
          ? `${
              item.cancellations[0].price.price > 0
                ? ` ${item.cancellations[0].price.currency}`
                : ''
            } ${formatPriceNumber(
              item.cancellations[0].price.currency === UNIT_PRICES.USD
                ? usdToCent(item.cancellations[0].price.price || 0)
                : item.cancellations[0].price.price || 0,
            )}`
          : 0,
    }))
    onUpdateCsvData(csvData)
  }, [i18n.language, listGoodsName, currentPage, size, listOrgs])

  const emptyRows = items.length === 0 ? 5 : 5 - items.length

  return (
    <Table className={classes.table} size='small'>
      <TableHead>
        <TableRow className={classes.tableHeader}>
          {tableHeader.map((header) => (
            <TableCell key={header.id} align='center' padding='none'>
              {header.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {items.length > 0
          ? items.map((item, index) => (
              <TableRow
                key={item.order.orderId}
                hover
                style={{cursor: 'pointer'}}
                onClick={() => onNavigateProductDetail(item.order.orderId)}
              >
                <TableCell align='center'>
                  {currentPage * size + (items.length - index)}
                </TableCell>
                <TableCell align='center'>
                  {getProductStatus(item.payment.status)}
                </TableCell>
                <TableCell align='center'>
                  {getOrganizationName(item.order.orgId)}
                </TableCell>
                <TableCell align='center'>
                  {item.payment.customer.email}
                </TableCell>
                <TableCell align='center'>
                  {getProductClassitication(item.order.orderItems[0].goodsType)}
                </TableCell>
                <TableCell align='center'>
                  {getGoodsName(item.order.orderId)}
                </TableCell>
                <TableCell align='center'>
                  {isoStringToDateAndTimeString(String(item.order.orderAt))}
                </TableCell>
                <TableCell align='center'>{item.payment.paymentNo}</TableCell>
                <TableCell align='center'>{`${
                  item.payment.price.price > 0
                    ? ` ${item.payment.price.currency}`
                    : ''
                } ${formatPriceNumber(
                  item.payment.price.currency === UNIT_PRICES.USD
                    ? usdToCent(item.payment.price.price)
                    : item.payment.price.price,
                )}`}</TableCell>
                <TableCell align='center'>
                  {item.cancellations.length > 0
                    ? `${
                        item.cancellations[0].price.price > 0
                          ? `${item.cancellations[0].price.currency}`
                          : ''
                      } ${formatPriceNumber(
                        item.cancellations[0].price.currency === UNIT_PRICES.USD
                          ? usdToCent(item.cancellations[0].price.price || 0)
                          : item.cancellations[0].price.price || 0,
                      )}`
                    : 0}
                </TableCell>
                <TableCell
                  align='center'
                  onClick={(event) => {
                    event.stopPropagation()
                    if (!item.cancellations.length) {
                      onCancelPurchase(item)
                    }
                  }}
                >
                  {item.cancellations.length > 0 ? '-' : t('ICancel')}
                </TableCell>
                <TableCell
                  align='center'
                  onClick={(event) => {
                    event.stopPropagation()
                    onShowInvoice(item)
                  }}
                >
                  {t('IInvoice')}
                </TableCell>
              </TableRow>
            ))
          : []}
        {emptyRows > 0 && (
          <TableRow
            style={{
              height: 43 * emptyRows,
              backgroundColor: '#F9F9FB',
            }}
          >
            <TableCell colSpan={14} />
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}

export default DataTable
